import React, { useState, useEffect, useRef } from 'react';

// prop-type is a library for typechecking of props
import PropTypes from 'prop-types';

// Argon Dashboard 2 PRO MUI components
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonInput from 'components/ArgonInput';
import TooltipWrapper from 'components/TooltipWrapper';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { getDecimalSeparator } from 'services/numberFormatting';
import { getGroupSeparator } from 'services/numberFormatting';

import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';

import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import flatpickr from 'flatpickr';
import { IconButton, InputAdornment } from '@mui/material';

// First, you need to create the `numberMask` with your desired configurations

const useStyles = makeStyles((theme) => ({
  container: {
    // display: 'flex',
    // flexWrap: 'wrap'
  },
  formControl: {
    // margin: theme.spacing(1)
  }
}));

// const numberMask = createNumberMask({
//   prefix: '',
//   suffix: ' $' // This will put the dollar sign at the end, with a space.
// });

// Create date pipe for YYYY-MM-DD format
const autoCorrectedDatePipe = createAutoCorrectedDatePipe('yyyy-mm-dd', {
  minYear: 1900,
  maxYear: 2099
});

// Create mask for YYYY-MM-DD format
const dateMask = [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/];

function TextMaskCustom(props) {
  const {
    inputRef,
    onEnterPressed,
    error,
    helperText,
    width,
    prefix, // prefix for the input
    suffix, // suffix for the input
    placeholder, // custom placeholder

    ...other
  } = props;

  console.log('TextMaskCustom - rendering date input');

  return (
    <MaskedInput
      {...other}
      ref={(ref) => {
        inputRef && inputRef(ref ? ref.inputElement : null);
      }}
      mask={dateMask}
      placeholderChar={'_'} // Unterstriche als besser sichtbare Platzhalter
      pipe={autoCorrectedDatePipe}
      guide={true}
      keepCharPositions={true}
      placeholder={placeholder || 'YYYY-MM-DD'} // Benutze benutzerdefinierten Platzhalter oder Standard
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          console.log('Enter key pressed');
          onEnterPressed && onEnterPressed(event.target.value);
        }
      }}
      error={error}
      helperText={helperText}
      style={{ width: width }}
    />
  );
}

TextMaskCustom.propTypes = {
  inputRef: PropTypes.func.isRequired
};

// Hilfsfunktion zur Formatierung von Datumswerten
const formatDateValue = (value) => {
  // Wenn der Wert undefined oder null ist, leeren String zurückgeben
  if (value === undefined || value === null) {
    console.log('formatDateValue: Wert ist undefined oder null');
    return '';
  }

  // Wenn der Wert bereits ein String ist
  if (typeof value === 'string') {
    console.log('formatDateValue: Wert ist bereits ein String:', value);
    return value;
  }

  // Wenn der Wert ein Date-Objekt ist
  if (value instanceof Date) {
    // Prüfen, ob das Datum gültig ist
    if (isNaN(value.getTime())) {
      console.log('formatDateValue: Ungültiges Date-Objekt');
      return '';
    }

    // Formatieren als YYYY-MM-DD
    const year = value.getFullYear();
    // Prüfen, ob das Jahr im gültigen Bereich liegt
    if (year < 1900 || year > 2099) {
      console.log(
        'formatDateValue: Jahr außerhalb des gültigen Bereichs:',
        year
      );
      return '';
    }

    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    console.log('formatDateValue: Date-Objekt formatiert als:', formattedDate);
    return formattedDate;
  }

  // Für alle anderen Typen, versuchen zu String zu konvertieren
  console.log('formatDateValue: Unbekannter Typ, versuche toString:', value);
  try {
    return String(value);
  } catch (error) {
    console.error(
      'formatDateValue: Fehler bei der Konvertierung zu String:',
      error
    );
    return '';
  }
};

function FormikDateTextInput({
  id,
  name,
  label,
  formik,
  tooltip,
  required,
  prefix,
  suffix,
  labelColor = 'textLight',
  size,
  placeholder,
  ...rest
}) {
  const { i18n } = useTranslation();
  const classes = useStyles();
  const inputRef = useRef(null);
  const fpRef = useRef(null); // Add ref to store flatpickr instance

  // let fp = null;

  // useEffect(() => {
  //   // Only initialize if we have a ref
  //   if (inputRef.current) {
  //     // fpRef.current = flatpickr(inputRef.current, {
  //     fp = flatpickr(inputRef.current, {
  //       dateFormat: 'Y-m-d',
  //       allowInput: true
  //     });

  //     console.log(
  //       'FormikDateTextInput - flatpickr instance created:',
  //       fpRef.current
  //     );

  //     // Cleanup on unmount
  //     return () => {
  //       if (fpRef.current) {
  //         fpRef.current.destroy();
  //         fpRef.current = null;
  //       }
  //     };
  //   }
  // }, []);

  // Sicherer Zugriff auf den Formik-Wert
  const rawValue = formik.values[name];
  console.log(
    'FormikDateTextInput - Rendering mit Rohwert:',
    rawValue,
    'Typ:',
    typeof rawValue
  );

  // Formatieren des Werts für die Anzeige
  const formattedValue = formatDateValue(rawValue);

  const handleChange = (event) => {
    event.preventDefault();

    let value = event.target.value;
    console.log(
      'FormikDateTextInput - handleChange - neuer Wert:',
      value,
      'Typ:',
      typeof value
    );

    try {
      // Prüfen, ob der Wert ein gültiges Datum im Format YYYY-MM-DD ist
      if (value && value.length === 10) {
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        if (datePattern.test(value)) {
          const [year, month, day] = value.split('-').map(Number);

          // Prüfen, ob das Datum gültig ist
          const date = new Date(year, month - 1, day);
          if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day ||
            year < 1900 ||
            year > 2099
          ) {
            console.warn(
              'FormikDateTextInput - handleChange - Ungültiges Datum:',
              value
            );
            // Trotzdem den Wert setzen, da die Validierung durch Formik erfolgen sollte
          } else {
            console.log(
              'FormikDateTextInput - handleChange - Gültiges Datum:',
              value
            );
          }
        }
      }
    } catch (error) {
      console.error(
        'FormikDateTextInput - handleChange - Fehler bei der Datumsvalidierung:',
        error
      );
    }

    // Wert in Formik setzen
    console.log('FormikDateTextInput - setting field value:', value);
    formik.setFieldValue(name, value, true);
  };

  const handleEnterPressed = (value) => {
    // when entered in european style, convert to US / number style
    // if (getDecimalSeparator(i18n.language) === ',') {
    //   // remove all dots
    //   value = value.replace(/\./g, '');
    //   // replace comma with dot
    //   value = value.replace(',', '.');
    // }
    // let numValue = Number(value);
    // console.log('Weight Input: enter pressed: new value', value, numValue);
    // onEnterPressed && onEnterPressed(value);
  };

  return (
    <>
      {label && (
        <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
          <TooltipWrapper tooltip={tooltip}>
            <ArgonTypography
              component="label"
              variant="caption"
              fontWeight="bold"
              textTransform="capitalize"
              color={labelColor}
              {...rest}>
              {label}&nbsp;
              {required && <span style={{ color: 'red' }}>*</span>}
              {tooltip && (
                <ArgonBox ml={1} display="inline-block">
                  <ArgonTypography variant="regular">
                    <InfoOutlinedIcon color="secondary" />
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonTypography>
          </TooltipWrapper>
        </ArgonBox>
      )}

      <ArgonInput
        id={id}
        name={name}
        className={classes.formControl}
        value={formattedValue}
        onChange={handleChange}
        onEnterPressed={handleEnterPressed}
        error={formik.touched[name] && Boolean(formik.errors[name])}
        helperText={formik.touched[name] && formik.errors[name]}
        inputComponent={TextMaskCustom}
        size={size}
        inputProps={{
          ref: inputRef,
          prefix,
          suffix,
          placeholder,
          onError: (error) => {
            console.error('TextMaskCustom - Fehler:', error);
          }
        }}
        variant="outlined"
        // endAdornment={
        //   <InputAdornment position="end">
        //     <IconButton
        //       onClick={() => {
        //         if (fpRef.current) {
        //           fpRef.current.open();
        //         }
        //       }}>
        //       <CalendarMonthIcon />
        //     </IconButton>
        //   </InputAdornment>
        // }
        {...rest}
      />
    </>
  );
}

// typechecking props for FormikDateTextInput
FormikDateTextInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  required: PropTypes.bool,
  textColor: PropTypes.string,
  formik: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  placeholder: PropTypes.string // Platzhalter für das Datumsfeld
};

export default FormikDateTextInput;
